import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PaperAirplaneIcon, PaperClipIcon, XMarkIcon } from '@heroicons/react/24/outline';
import axios from 'axios';
import AnimatedModal from '../ui/AnimatedModal';

// Kept in step with server/utils/noteAttachmentStorage.js. Validating here is
// purely for fast feedback — the server re-checks every byte and is the only
// thing standing between a crafted upload and the bucket.
const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png'];
const ACCEPT_ATTR = '.jpg,.jpeg,.png,image/jpeg,image/png';
const MAX_ATTACHMENTS = 4;
const MAX_ATTACHMENT_BYTES = 5 * 1024 * 1024;

const isAcceptedImage = (file) =>
  !!file && ACCEPTED_IMAGE_TYPES.includes(String(file.type || '').toLowerCase());

const formatBytes = (bytes) => {
  const n = Number(bytes) || 0;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const formatMessageTime = (iso) => {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const now = new Date();
  const isToday =
    date.getDate() === now.getDate() &&
    date.getMonth() === now.getMonth() &&
    date.getFullYear() === now.getFullYear();
  if (isToday) {
    return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  }
  return date.toLocaleDateString([], {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const initialsFor = (name) => {
  const parts = String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const isOwnMessage = (note, { currentUserId, currentUserName, reviewerName }) => {
  if (note.authorUserId && currentUserId) {
    return String(note.authorUserId) === String(currentUserId);
  }
  const author = String(note.authorName || '').trim().toLowerCase();
  const selfName = String(currentUserName || reviewerName || '')
    .trim()
    .toLowerCase();
  return !!author && !!selfName && author === selfName;
};

export default function CandidateNotesModal({
  isOpen,
  onClose,
  candidateId,
  candidateName,
  screeningId,
  shareCode = null,
  reviewerName = '',
  currentUserId = null,
  currentUserName = '',
  onNoteAdded = null,
}) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState('');
  // Staged images: { id, file, previewUrl }. previewUrl is an object URL that
  // must be revoked, so every removal path goes through removePending().
  const [pending, setPending] = useState([]);
  const [lightbox, setLightbox] = useState(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const pendingRef = useRef([]);

  // Mirror `pending` into a ref so the unmount cleanup below can revoke the
  // object URLs without re-running (and revoking live previews) on every change.
  useEffect(() => {
    pendingRef.current = pending;
  }, [pending]);

  useEffect(
    () => () => {
      pendingRef.current.forEach((p) => URL.revokeObjectURL(p.previewUrl));
    },
    []
  );

  const identity = useMemo(
    () => ({ currentUserId, currentUserName, reviewerName }),
    [currentUserId, currentUserName, reviewerName]
  );

  const scrollToBottom = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, []);

  const fetchNotes = useCallback(async () => {
    if (!candidateId) return;
    setLoading(true);
    setError('');
    try {
      const url = shareCode
        ? `/api/screenings/public/candidate-review/${shareCode}/candidates/${candidateId}/notes`
        : `/api/screenings/${screeningId}/candidates/${candidateId}/notes`;
      const res = await axios.get(url);
      setNotes(Array.isArray(res.data?.notes) ? res.data.notes : []);
    } catch (err) {
      setError(err.response?.data?.message || 'Could not load notes');
      setNotes([]);
    } finally {
      setLoading(false);
    }
  }, [candidateId, screeningId, shareCode]);

  useEffect(() => {
    if (!isOpen || !candidateId) return;
    setDraft('');
    setPending((prev) => {
      prev.forEach((p) => URL.revokeObjectURL(p.previewUrl));
      return [];
    });
    setLightbox(null);
    fetchNotes();
  }, [isOpen, candidateId, fetchNotes]);

  useEffect(() => {
    if (!loading && isOpen) {
      scrollToBottom();
      setTimeout(() => inputRef.current?.focus(), 80);
    }
  }, [loading, isOpen, notes.length, scrollToBottom]);

  const addFiles = useCallback(
    (fileList) => {
      const incoming = Array.from(fileList || []);
      if (incoming.length === 0) return;

      const rejected = [];
      const accepted = [];

      incoming.forEach((file) => {
        if (!isAcceptedImage(file)) {
          rejected.push(`${file.name || 'file'} is not a JPG or PNG`);
        } else if (file.size > MAX_ATTACHMENT_BYTES) {
          rejected.push(`${file.name || 'file'} is larger than 5MB`);
        } else {
          accepted.push(file);
        }
      });

      setPending((prev) => {
        const room = MAX_ATTACHMENTS - prev.length;
        if (accepted.length > room) {
          rejected.push(`You can attach up to ${MAX_ATTACHMENTS} images per note`);
        }
        const next = accepted.slice(0, Math.max(room, 0)).map((file) => ({
          id: `${file.name}-${file.size}-${file.lastModified}-${Math.random().toString(36).slice(2)}`,
          file,
          previewUrl: URL.createObjectURL(file),
        }));
        return next.length ? [...prev, ...next] : prev;
      });

      setError(rejected.length ? rejected[0] : '');
    },
    []
  );

  const removePending = useCallback((id) => {
    setPending((prev) => {
      const target = prev.find((p) => p.id === id);
      if (target) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((p) => p.id !== id);
    });
  }, []);

  const handleFilePick = (e) => {
    addFiles(e.target.files);
    // Reset so re-picking the same file still fires a change event.
    e.target.value = '';
  };

  // Pasting a screenshot straight into the composer is the main way these images
  // will arrive, so it is wired to the same validation as the file picker.
  const handlePaste = (e) => {
    const files = Array.from(e.clipboardData?.files || []);
    const images = files.filter((f) => String(f.type || '').startsWith('image/'));
    if (images.length === 0) return;
    e.preventDefault();
    addFiles(images);
  };

  const handleSend = async () => {
    const body = draft.trim();
    if ((!body && pending.length === 0) || sending) return;

    if (shareCode && !reviewerName) {
      setError('Please enter your name before adding a note.');
      return;
    }

    setSending(true);
    setError('');
    try {
      const url = shareCode
        ? `/api/screenings/public/candidate-review/${shareCode}/candidates/${candidateId}/notes`
        : `/api/screenings/${screeningId}/candidates/${candidateId}/notes`;
      // Multipart only when images are staged, so the text-only path keeps
      // sending the exact JSON body it always has.
      let res;
      if (pending.length > 0) {
        const form = new FormData();
        form.append('body', body);
        if (shareCode) form.append('reviewerName', reviewerName);
        pending.forEach((p) => form.append('images', p.file, p.file.name));
        // Content-Type is left unset on purpose — the browser has to add the
        // multipart boundary, and setting it by hand strips it.
        res = await axios.post(url, form);
      } else {
        res = await axios.post(url, shareCode ? { body, reviewerName } : { body });
      }
      const created = res.data?.note;
      if (created) {
        setNotes((prev) => {
          if (prev.some((n) => n.id === created.id)) return prev;
          return [...prev, created];
        });
        onNoteAdded?.(candidateId, created.body);
      } else {
        await fetchNotes();
      }
      setDraft('');
      setPending((prev) => {
        prev.forEach((p) => URL.revokeObjectURL(p.previewUrl));
        return [];
      });
      scrollToBottom();
    } catch (err) {
      if (err?.response?.data?.code === 'REVIEWER_NAME_REQUIRED') {
        setError('Please enter your name before adding a note.');
      } else {
        setError(err.response?.data?.message || 'Could not send note');
      }
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const canSend = !sending && (!!draft.trim() || pending.length > 0);

  // Attachment URLs are presigned and expire (1h). If the modal has been left
  // open past that, a thumbnail 403s — refetching mints fresh URLs. Guarded by a
  // ref so a genuinely broken object cannot spin this into a refetch loop.
  const retriedUrlsRef = useRef(new Set());
  const handleImageError = useCallback(
    (url) => {
      if (!url || retriedUrlsRef.current.has(url)) return;
      retriedUrlsRef.current.add(url);
      fetchNotes();
    },
    [fetchNotes]
  );

  return (
    <AnimatedModal
      isOpen={isOpen}
      onClose={() => {
        if (sending) return;
        // While a preview is open, a backdrop dismiss means "close the preview",
        // not "throw away the note I was writing" — so it absorbs the first one.
        if (lightbox) {
          setLightbox(null);
          return;
        }
        onClose();
      }}
      contentClassName="bg-white rounded-2xl shadow-2xl w-full max-w-lg border border-gray-100 overflow-hidden"
      zIndex={10000}
    >
      <div className="flex flex-col max-h-[min(80vh,640px)]">
        <div className="flex-shrink-0 px-5 py-4 border-b border-gray-100 bg-gradient-to-r from-white to-[#F7FAFF]">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h3 className="text-base font-semibold text-gray-900">Notes</h3>
              {candidateName && (
                <p className="mt-0.5 text-xs text-gray-500 truncate">{candidateName}</p>
              )}
            </div>
            <button
              type="button"
              disabled={sending}
              onClick={onClose}
              className="rounded-lg p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors disabled:opacity-50"
              aria-label="Close notes"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div
          ref={scrollRef}
          className="flex-1 overflow-y-auto px-4 py-4 bg-[#F7F9FC] min-h-[280px]"
        >
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <span className="inline-block w-6 h-6 border-2 border-[#007BFF] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : notes.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-14 px-6 text-center">
              <div className="h-12 w-12 rounded-2xl bg-white border border-gray-200 shadow-sm flex items-center justify-center mb-3">
                <span className="text-lg">💬</span>
              </div>
              <p className="text-sm font-medium text-gray-700">No notes yet</p>
              <p className="mt-1 text-xs text-gray-500 max-w-[240px]">
                Start a conversation with your team about this candidate.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {notes.map((note) => {
                const mine = isOwnMessage(note, identity);
                return (
                  <div
                    key={note.id}
                    className={`flex gap-2.5 ${mine ? 'flex-row-reverse' : 'flex-row'}`}
                  >
                    <div
                      className={`flex-shrink-0 h-8 w-8 rounded-full text-[11px] font-semibold flex items-center justify-center shadow-sm ${
                        mine
                          ? 'bg-[#007BFF] text-white'
                          : note.source === 'share_link'
                            ? 'bg-emerald-100 text-emerald-700'
                            : 'bg-violet-100 text-violet-700'
                      }`}
                    >
                      {initialsFor(note.authorName)}
                    </div>
                    <div className={`min-w-0 max-w-[78%] ${mine ? 'items-end' : 'items-start'} flex flex-col`}>
                      <div
                        className={`flex flex-wrap items-center gap-x-2 gap-y-0.5 mb-1 ${
                          mine ? 'justify-end' : 'justify-start'
                        }`}
                      >
                        <span className="text-[11px] font-semibold text-gray-700">
                          {mine ? 'You' : note.authorName || 'Team member'}
                        </span>
                        <span className="text-[10px] text-gray-400">
                          {formatMessageTime(note.createdAt)}
                        </span>
                        {!mine && note.source === 'share_link' && (
                          <span className="inline-flex items-center rounded-full bg-emerald-50 px-1.5 py-0.5 text-[9px] font-medium text-emerald-700 border border-emerald-100">
                            Reviewer
                          </span>
                        )}
                        {!mine && note.source === 'recruiter' && (
                          <span className="inline-flex items-center rounded-full bg-violet-50 px-1.5 py-0.5 text-[9px] font-medium text-violet-700 border border-violet-100">
                            Recruiter
                          </span>
                        )}
                      </div>
                      {!!note.body && (
                        <div
                          className={`rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed shadow-sm whitespace-pre-wrap break-words ${
                            mine
                              ? 'bg-[#007BFF] text-white rounded-tr-md'
                              : 'bg-white text-gray-800 border border-gray-100 rounded-tl-md'
                          }`}
                        >
                          {note.body}
                        </div>
                      )}
                      {Array.isArray(note.attachments) && note.attachments.length > 0 && (
                        <div
                          className={`mt-1.5 grid gap-1.5 ${
                            note.attachments.length === 1 ? 'grid-cols-1' : 'grid-cols-2'
                          }`}
                        >
                          {note.attachments.map((att, idx) => (
                            <button
                              key={`${note.id}-att-${idx}`}
                              type="button"
                              onClick={() => setLightbox(att)}
                              className="group relative block overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-[#007BFF]/40"
                              title={`${att.fileName || 'image'} — click to enlarge`}
                            >
                              <img
                                src={att.url}
                                alt={att.fileName || 'Note attachment'}
                                loading="lazy"
                                onError={() => handleImageError(att.url)}
                                className="h-32 w-full object-cover transition-transform duration-200 group-hover:scale-[1.03]"
                              />
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex-shrink-0 border-t border-gray-100 bg-white px-4 py-3">
          {error && (
            <p className="mb-2 text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
              {error}
            </p>
          )}
          {pending.length > 0 && (
            <div className="mb-2.5 flex flex-wrap gap-2">
              {pending.map((p) => (
                <div
                  key={p.id}
                  className="relative h-16 w-16 overflow-hidden rounded-lg border border-gray-200 bg-gray-50 shadow-sm"
                  title={`${p.file.name} · ${formatBytes(p.file.size)}`}
                >
                  <img src={p.previewUrl} alt={p.file.name} className="h-full w-full object-cover" />
                  <button
                    type="button"
                    onClick={() => removePending(p.id)}
                    disabled={sending}
                    className="absolute right-0.5 top-0.5 rounded-full bg-gray-900/70 p-0.5 text-white hover:bg-gray-900 disabled:opacity-50"
                    aria-label={`Remove ${p.file.name}`}
                  >
                    <XMarkIcon className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
          <div className="flex items-end gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPT_ATTR}
              multiple
              onChange={handleFilePick}
              className="hidden"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={sending || pending.length >= MAX_ATTACHMENTS}
              className="inline-flex h-[42px] w-[42px] flex-shrink-0 items-center justify-center rounded-xl border border-gray-200 bg-gray-50 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
              aria-label="Attach an image"
              title={
                pending.length >= MAX_ATTACHMENTS
                  ? `Up to ${MAX_ATTACHMENTS} images per note`
                  : 'Attach a JPG or PNG image'
              }
            >
              <PaperClipIcon className="h-4 w-4" />
            </button>
            <textarea
              ref={inputRef}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              disabled={sending}
              rows={1}
              placeholder="Write a note…"
              className="flex-1 max-h-28 min-h-[42px] resize-none rounded-xl border border-gray-200 bg-gray-50 px-3.5 py-2.5 text-sm text-gray-800 placeholder-gray-400 focus:bg-white focus:border-[#007BFF] focus:outline-none focus:ring-2 focus:ring-[#007BFF]/20 transition-all disabled:opacity-60"
            />
            <button
              type="button"
              onClick={handleSend}
              disabled={!canSend}
              className="inline-flex h-[42px] w-[42px] flex-shrink-0 items-center justify-center rounded-xl bg-[#007BFF] text-white shadow-sm hover:bg-[#0062CC] transition-colors disabled:bg-[#007BFF]/40 disabled:cursor-not-allowed"
              aria-label="Send note"
            >
              {sending ? (
                <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <PaperAirplaneIcon className="h-4 w-4" />
              )}
            </button>
          </div>
          <p className="mt-2 text-[10px] text-gray-400 text-center">
            Enter to send · Shift+Enter for new line · paste or attach JPG/PNG up to 5MB
          </p>
        </div>
      </div>

      {/* Full-size preview. Rendered inside the modal so it inherits the
          AnimatedModal stacking context and cannot appear behind it. */}
      {lightbox && (
        <div
          role="presentation"
          onClick={() => setLightbox(null)}
          className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-gray-900/85 p-4 backdrop-blur-sm"
        >
          <img
            src={lightbox.url}
            alt={lightbox.fileName || 'Note attachment'}
            onClick={(e) => e.stopPropagation()}
            className="max-h-[70vh] max-w-full rounded-lg object-contain shadow-2xl"
          />
          <div className="flex items-center gap-3 text-[11px] text-white/80">
            <span className="truncate max-w-[220px]">{lightbox.fileName || 'image'}</span>
            {!!lightbox.size && <span>{formatBytes(lightbox.size)}</span>}
            <a
              href={lightbox.url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="underline hover:text-white"
            >
              Open original
            </a>
          </div>
          <button
            type="button"
            onClick={() => setLightbox(null)}
            className="absolute right-3 top-3 rounded-lg p-1.5 text-white/70 transition-colors hover:bg-white/10 hover:text-white"
            aria-label="Close preview"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
      )}
    </AnimatedModal>
  );
}
